import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const AnimatedCounter = ({
  end,
  duration = 2000,
  prefix = '',
  suffix = '',
  className = '',
  glowColor = 'rgba(83, 150, 189, 0.6)',
}) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
      }
    };

    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return (
    <span ref={ref} className={`relative inline-block ${className}`}>
      <motion.span
        animate={isComplete ? { scale: [1, 1.1, 1] } : {}}
        transition={{ duration: 0.3 }}
        className="relative z-10"
        style={{
          textShadow: hasStarted ? `0 0 20px ${glowColor}` : 'none',
          transition: 'text-shadow 0.5s ease',
        }}
      >
        {prefix}{count.toLocaleString()}{suffix}
      </motion.span>
      {hasStarted && (
        <span
          className="absolute inset-0 -z-10 rounded-full blur-2xl opacity-20"
          style={{
            background: `radial-gradient(circle, ${glowColor}, transparent 70%)`,
          }}
        />
      )}
    </span>
  );
};

export default AnimatedCounter;
